import { Component, OnInit } from '@angular/core';
import {
  CustomerClient,
  DocumentClient,
  DocumentShortInfoDto,
  DocumentType,
  PetClient,
  PetDto,
  TreatmentClient,
  TreatmentDto, UpdateTreatmentRequestDto
} from '../../../api/api-client';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslationPipe } from '../../../pipes/translation.pipe';
import { DashboardTitleService } from '../../../services/dashboard-title.service';
import { DateTime } from 'luxon';

import { Button } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TabsModule } from 'primeng/tabs';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { EditorModule } from 'primeng/editor';
import { AddDocumentDialogComponent } from '../../dialogs/add-document-dialog/add-document-dialog.component';
import { ViewDocumentDialogComponent } from '../../dialogs/view-document-dialog/view-document-dialog.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SafeHtmlPipe } from '../../../pipes/safe-html.pipe';
import { FeedbackDataService } from '../../../services/feedback-data.service';
import { FeedbackData } from '../../../models/feedback-data';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { isFormControlInvalid } from '../../../util/form-control-invalid-checker';
import { DeleteDocumentDialogComponent } from '../../dialogs/delete-document-dialog/delete-document-dialog.component';
import { AttachmentGalleryComponent } from '../../widgets/attachment-gallery/attachment-gallery.component';
import { CardWithHeaderComponent } from '../../widgets/card-with-header/card-with-header.component';
import { SubscriptionManager } from '../../../services/subscription-manager.service';
import { ViewDocumentRoutingInfo } from '../../../models/view-document-routing-info';

@Component({
  selector: 'app-treatment-detail',
  imports: [
    RippleModule,
    ScrollPanelModule,
    TabsModule,
    TranslationPipe,
    EditorModule,
    FormsModule,
    Button,
    SafeHtmlPipe,
    DatePickerModule,
    ReactiveFormsModule,
    InputTextModule,
    AttachmentGalleryComponent,
    CardWithHeaderComponent
  ],
  providers: [SubscriptionManager],
  templateUrl: './treatment-detail.component.html',
  styleUrl: './treatment-detail.component.scss'
})
export class TreatmentDetailComponent implements OnInit {

  treatmentId!: string;
  treatment!: TreatmentDto;
  pet!: PetDto;

  notPinnedDocuments: DocumentShortInfoDto[] = [];
  notPinnedImages: DocumentShortInfoDto[] = [];
  pinnedDocuments: DocumentShortInfoDto[] = [];
  pinnedImages: DocumentShortInfoDto[] = [];


  treatmentDate!: Date;

  pageMode: 'view' | 'edit' = 'view';

  refAddDocumentDialog!: DynamicDialogRef<AddDocumentDialogComponent> | null;
  refViewDocumentDialog!: DynamicDialogRef<ViewDocumentDialogComponent> | null;
  refDeleteDocumentDialog!: DynamicDialogRef<DeleteDocumentDialogComponent> | null;

  constructor(
    private readonly subscriptionManager: SubscriptionManager,
    private readonly treatmentService: TreatmentClient,
    private readonly petService: PetClient,
    private readonly customerService: CustomerClient,
    private readonly documentService: DocumentClient,
    private readonly route: ActivatedRoute,
    private readonly dialogService: DialogService,
    private readonly dashboardService: DashboardTitleService,
    private readonly router: Router,
    private readonly feedbackService: FeedbackDataService) {
  }

  ngOnInit(): void {
    this.notPinnedDocuments = [];
    this.notPinnedImages = [];
    this.pinnedDocuments = [];
    this.pinnedImages = [];
    this.subscriptionManager.call(
      this.route.paramMap, (paramMap => {
        this.treatmentId = paramMap.get('treatmentId') ?? '';
      })
    );

    this.subscriptionManager.call(
      this.treatmentService.getTreatmentById(this.treatmentId),
      (x => {
        if (x.status == 200) {
          this.treatment = x.result;
          this.treatmentDate = this.treatment.appointmentDate.toJSDate();
        }
        this.subscriptionManager.call(
          this.petService.getPetById(this.treatment.patientId),
          (x => {
            if (x.status == 200) {
              this.pet = x.result;
              this.subscriptionManager.call(
                this.customerService.getCustomerById(this.pet.ownerId),
                (y => {
                  this.dashboardService.breadCrumbData.next({
                    petName: x.result.nickname,
                    petId: x.result.id,
                    customerFirstname: y.result.firstName,
                    customerLastname: y.result.lastName,
                    customerId: x.result.ownerId,
                    treatmentDate: this.treatment.appointmentDate,
                    treatmentId: this.treatmentId,
                    treatmentTitle: this.treatment.title
                  });
                })
              );
              this.feedbackService.feedbackData = new FeedbackData();
              this.feedbackService.feedbackData.customerId = this.pet.ownerId;
              this.feedbackService.feedbackData.petId = this.pet.id;
              this.feedbackService.feedbackData.treatmentId = this.treatmentId;
            }
          })
        );
        this.subscriptionManager.call(
          this.documentService.getPinnedDocumentShortInfosForPet(this.treatment.patientId),
          (x => {
            if (x.status == 200) {
              this.pinnedDocuments = [...this.pinnedDocuments, ...x.result.filter(y => y.documentType === 'Document')];
              this.pinnedImages = [...this.pinnedImages, ...x.result.filter(y => y.documentType === 'Image')];
            }
          })
        );
        this.subscriptionManager.call(
          this.documentService.getDocumentShortInfosForTreatment(this.treatment.id),
          (x => {
            if (x.status == 200) {
              this.notPinnedDocuments = [...this.notPinnedDocuments, ...x.result.filter(y => y.documentType === 'Document')];
              this.notPinnedImages = [...this.notPinnedImages, ...x.result.filter(y => y.documentType === 'Image')];
            }
          })
        );
      })
    );
  }

  openAddDocumentDialog(docType: DocumentType, isSkeleton = false) {
    this.refAddDocumentDialog = this.dialogService.open(AddDocumentDialogComponent, {
      modal: true,
      closeOnEscape: true,
      closable: true,
      data: {
        sourcePage: 'treatment-detail',
        documentType: docType,
        entityId: this.treatment.id,
        isSkeleton: isSkeleton
      }
    });
    if (this.refAddDocumentDialog) {
      this.subscriptionManager.call(
        this.refAddDocumentDialog.onClose,
        (() => {
            this.ngOnInit();
          }
        )
      );
    }
  }

  openViewDocumentDialog(documentId: string) {
    this.subscriptionManager.call(
      this.documentService.getDocumentById(documentId),
      (x => {
        this.refViewDocumentDialog = this.dialogService.open(ViewDocumentDialogComponent, {
          modal: true,
          closeOnEscape: true,
          closable: true,
          header: x.result.filename,
          data: {
            document: x.result
          } as ViewDocumentRoutingInfo
        });
      })
    );
  }


  openEditDocumentPage(document: DocumentShortInfoDto) {
    this.subscriptionManager.call(
      this.documentService.getDocumentById(document.id),
      (x => {
        this.router.navigate(['../loggedin/images/annotate'], {
          state: {
            routingData: {
              sourcePage: 'treatment-detail',
              documentType: x.result.documentType,
              entityId: this.treatment.id
            },
            addedDocument: x.result
          }
        });
      })
    );

  }

  protected openDeleteDocumentDialog(document: DocumentShortInfoDto) {
    this.refDeleteDocumentDialog = this.dialogService.open(DeleteDocumentDialogComponent, {
      modal: true,
      closeOnEscape: true,
      closable: true,
      data: {
        entityId: document.id,
        filename: document.filename,
        documentType: document.documentType
      }
    });
    if (this.refDeleteDocumentDialog) {
      this.subscriptionManager.call(
        this.refDeleteDocumentDialog?.onClose,
        (x => {
          if (x) {
            this.notPinnedDocuments = this.notPinnedDocuments.filter(x => x.id != document.id);
            this.notPinnedImages = this.notPinnedImages.filter(x => x.id != document.id);
          }
        })
      );
    }
  }

  protected enterEditMode() {
    this.pageMode = 'edit';
  }

  protected saveTreatment() {
    this.treatment.description = this.treatment.description.replaceAll('&nbsp;', ' ');
    this.subscriptionManager.call(
      this.treatmentService.updateTreatment({
        id: this.treatment.id,
        description: this.treatment.description,
        appointmentDate: DateTime.fromJSDate(this.treatmentDate),
        title: this.treatment.title
      } as UpdateTreatmentRequestDto),
      (x => {
        if (x.status == 200) {
          const currentBreadCrumbData = this.dashboardService.breadCrumbData.getValue();
          currentBreadCrumbData.treatmentDate = DateTime.fromJSDate(this.treatmentDate);
          this.dashboardService.breadCrumbData.next(currentBreadCrumbData);
          this.pageMode = 'view';
        }
      })
    );
  }


  protected readonly isFormControlInvalid = isFormControlInvalid;
}
